import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { IFieldValue, IRegistrationField, MULTIVALUE_TYPES } from '../../registration-form.service';
import {
  ConditionEntry,
  ConditionPendingOp,
  NEW_FIELD_PLACEHOLDER_ID,
} from '../edit-registration-field-conditions/edit-registration-field-conditions.component';

@Component({
  selector: 'edit-registration-field-single-condition',
  templateUrl: './edit-registration-field-single-condition.component.html',
  styleUrls: ['./edit-registration-field-single-condition.component.scss'],
})
export class EditRegistrationFieldSingleConditionComponent implements OnInit, OnChanges {
  @Input() field: IRegistrationField | null = null;
  @Input() allFields: IRegistrationField[] = [];
  @Output() pendingOpsChange = new EventEmitter<ConditionPendingOp[]>();

  public pendingOps: ConditionPendingOp[] = [];

  // Current UI selection
  public selectedFieldId: number | null = null;
  public selectedValueId: number | null = null;

  // The condition that existed when the component was initialized / fields last changed
  private origFieldId: number | null = null;
  public origValueId: number | null = null;

  ngOnInit() {
    this.initFromConditions();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.field || changes.allFields) {
      this.pendingOps = [];
      this.pendingOpsChange.emit(this.pendingOps);
      this.initFromConditions();
    }
  }

  /** option_id of the current field, or a placeholder while it hasn't been saved yet */
  private get currentFieldId(): number {
    return this.field ? this.field.option_id : NEW_FIELD_PLACEHOLDER_ID;
  }

  private initFromConditions() {
    const conditions = this.computeConditions();
    const first = conditions[0] || null;
    this.origFieldId = first ? first.field.option_id : null;
    this.origValueId = first ? first.value.option_value_id : null;
    this.selectedFieldId = this.origFieldId;
    this.selectedValueId = this.origValueId;
  }

  private computeConditions(): ConditionEntry[] {
    const currentId = this.currentFieldId;
    const entries: ConditionEntry[] = [];
    for (const f of this.allFields) {
      if (!f.values) continue;
      for (const v of f.values) {
        const qtd = v.questions_to_display || [];
        if (qtd.indexOf(currentId) >= 0) {
          entries.push({ field: f, value: v });
        }
      }
    }
    return entries;
  }

  /** Selectable fields: multivalue fields, not the current field, and not unused default fields */
  get selectableFields(): IRegistrationField[] {
    const field = this.field;
    return this.allFields.filter(f =>
      (!field || f.option_id !== field.option_id)
      && MULTIVALUE_TYPES.indexOf(f.field_type) >= 0
      && !(f.is_default && f.is_in_use === false)
    );
  }

  get selectedField(): IRegistrationField | null {
    if (this.selectedFieldId == null) return null;
    return this.allFields.find(f => f.option_id === this.selectedFieldId) || null;
  }

  get selectedFieldValues(): IFieldValue[] {
    return (this.selectedField && this.selectedField.values) ? this.selectedField.values : [];
  }

  private findValue(fieldId: number | null, valueId: number | null): IFieldValue | null {
    if (fieldId == null || valueId == null) return null;
    const f = this.allFields.find(ff => ff.option_id === fieldId);
    if (!f || !f.values) return null;
    return f.values.find(v => v.option_value_id === valueId) || null;
  }

  onFieldChange() {
    this.selectedValueId = null;
    this.pendingOps = [];
    this.pendingOpsChange.emit(this.pendingOps);
  }

  removeCondition() {
    if (this.origValueId == null) return;

    const currentId = this.currentFieldId;
    const oldValue = this.findValue(this.origFieldId, this.origValueId);

    this.selectedFieldId = null;
    this.selectedValueId = null;

    if (oldValue) {
      const origQtd = oldValue.questions_to_display ? [...oldValue.questions_to_display] : [];
      const newQtd = origQtd.filter(id => id !== currentId);
      this.pendingOps = [{ kind: 'remove', valueId: oldValue.option_value_id, questionIds: newQtd }];
    } else {
      this.pendingOps = [];
    }

    this.pendingOpsChange.emit(this.pendingOps);
  }

  onValueChange() {
    const currentId = this.currentFieldId;

    const sameAsOriginal = this.selectedFieldId === this.origFieldId
      && this.selectedValueId === this.origValueId;

    if (sameAsOriginal) {
      this.pendingOps = [];
      this.pendingOpsChange.emit(this.pendingOps);
      return;
    }

    const ops: ConditionPendingOp[] = [];

    if (this.selectedValueId != null) {
      const newValue = this.findValue(this.selectedFieldId, this.selectedValueId);
      if (newValue) {
        const origQtd = newValue.questions_to_display ? [...newValue.questions_to_display] : [];
        const newQtd = origQtd.indexOf(currentId) >= 0 ? origQtd : [...origQtd, currentId];
        ops.push({ kind: 'add', valueId: newValue.option_value_id, questionIds: newQtd });
      }
    }

    if (this.origValueId != null && this.origValueId !== this.selectedValueId) {
      const oldValue = this.findValue(this.origFieldId, this.origValueId);
      if (oldValue) {
        const origQtd = oldValue.questions_to_display ? [...oldValue.questions_to_display] : [];
        const newQtd = origQtd.filter(id => id !== currentId);
        ops.push({ kind: 'remove', valueId: oldValue.option_value_id, questionIds: newQtd });
      }
    }

    this.pendingOps = ops;
    this.pendingOpsChange.emit(this.pendingOps);
  }
}



// WEBPACK FOOTER //
// ./src/app/modules/sessions/registration-form/edit-registration-field-single-condition/edit-registration-field-single-condition.component.ts